// Potree runtime façade — see docs/design-briefs/A7-potree-runtime-integration.md
//
// The Potree package itself is NOT bundled today. This file is the lazy
// import boundary: the adapter goes through `loadPotreeRuntime()` and only
// the loader knows about the package name. When the install lands (A12 or
// the ops PR that follows), the dynamic import resolves and the adapter
// transitions to `ready` without any other call-site changes.
//
// The runtime is wrapped in a small façade (`PotreeRuntime`) so the adapter
// can program against a stable surface. The real `@pnext/three-loader` /
// `potree-core` API lives behind the façade and never leaks into the
// adapter or the tool-card.

/**
 * Bounding box union returned by `getCombinedBounds()`. Shape matches the
 * `Giro3DAdapter.getModelBounds()` return so the right-rail tool card can
 * use a single fit-to-data routine across engines.
 */
export interface PotreeBounds {
  readonly center: [number, number, number];
  readonly size: [number, number, number];
  readonly diagonal: number;
}

/** Quality knobs surfaced by the right-rail `<PointCloudToolCard />`. */
export interface PotreeRuntimeQuality {
  /** Maximum points kept resident; null = engine default. */
  pointBudget: number | null;
  /** 0 = adaptive screen-space size; >0 = fixed size in CSS pixels. */
  pointSize: number;
  /** Fraction of the screen-space error threshold for octree subdivision. */
  subdivisionThreshold: number;
  /** Eye-dome / occlusion shading toggle. */
  occlusion: boolean;
  /** ASPRS class codes to keep (empty = show all). */
  classificationMask: ReadonlyArray<number>;
}

/** Camera surface the runtime drives during `fitTo()`. */
export interface PotreeCameraSurface {
  readonly position: { x: number; y: number; z: number };
  readonly target: { x: number; y: number; z: number };
}

/** Typed reason for `failed-to-load` / `failed-during-render`. */
export interface PotreeRuntimeError {
  readonly code:
    | 'POTREE_NOT_INSTALLED'
    | 'POTREE_IMPORT_FAILED'
    | 'POTREE_RENDER_FAILED';
  readonly message: string;
  readonly cause?: unknown;
}

/**
 * Stable façade the adapter programs against. The concrete implementation
 * is constructed by `loadPotreeRuntime()` after the dynamic import
 * succeeds.
 *
 * Methods are intentionally synchronous — heavy work (loading metadata
 * for a cloud) is funnelled through `loadCloud()` which is async.
 */
export interface PotreeRuntime {
  /** Apply quality knobs to every loaded cloud. */
  applyQuality(quality: PotreeRuntimeQuality): void;
  /** Apply classification mask to every loaded cloud. */
  applyClassificationMask(classes: ReadonlyArray<number>): void;
  /** Compute the bounding-box union across loaded clouds, or `null` when empty. */
  getCombinedBounds(): PotreeBounds | null;
  /** Frame the camera on the supplied bounds. Returns `false` when no camera surface is wired. */
  fitTo(bounds: PotreeBounds): boolean;
  /** Load a Potree cloud (metadata.json or octree URL). */
  loadCloud(input: { layerId: string; url: string }): Promise<void>;
  /** Remove a previously loaded cloud. */
  removeCloud(layerId: string): void;
  /** Number of loaded clouds — used by the adapter snapshot. */
  cloudCount(): number;
  /** Optional teardown hook, invoked from `unmount()`. */
  dispose?(): void;
}

/**
 * Function shape the adapter accepts in its constructor. Returning `null`
 * is the canonical "package not installed" signal — it is NOT an error.
 * Throwing should be reserved for genuine failures (network, bad chunk,
 * incompatible version) which surface as `failed-to-load`.
 */
export type PotreeRuntimeLoader = () => Promise<PotreeRuntime | null>;

/**
 * Default loader. Today the package is not present in `package.json`
 * (TODO(A12): install `@pnext/three-loader` or `potree-core` once the
 * runtime ops PR lands). The dynamic import is wrapped in a `Function`
 * indirection so Vite/esbuild does not statically resolve the missing
 * module at build time — without that wrap the build would fail when
 * the package is absent.
 *
 * When the package install lands, swap the inner string and the loader
 * starts producing a real `PotreeRuntime` adapter object. No other call
 * site changes.
 */
export const defaultPotreeRuntimeLoader: PotreeRuntimeLoader = async () => {
  try {
    // TODO(A12): once `@pnext/three-loader` (preferred) or `potree-core`
    // is added to package.json, replace the Function-wrapped import with
    // a static `await import('@pnext/three-loader')` and construct the
    // façade below from the loader's `Potree`/`PointCloudOctree` exports.
    const mod = await (
      Function('return import("@pnext/three-loader")')() as Promise<unknown>
    ).catch(() => null);
    if (!mod) return null;
    return adaptPotreeModule(mod);
  } catch (err) {
    if (isModuleResolutionFailure(err)) return null;
    throw err;
  }
};

/**
 * Wrap a freshly-imported Potree module in our façade. Defensive: even
 * if the module resolves but is missing the symbols we need (different
 * version, future API churn), we treat it as not-installed rather than
 * crashing the host.
 */
function adaptPotreeModule(mod: unknown): PotreeRuntime | null {
  if (!isObject(mod)) return null;
  // The real API surface lands with the A12 install. Today we return
  // `null` so the host stays in `not-installed`. Once the package is
  // present the façade construction code is filled in here.
  return null;
}

/**
 * Recognise the "module cannot be resolved" failure modes shipped by
 * Vite, Node ESM, and the browser dynamic-import shim. These are all
 * silent `not-installed` outcomes — they must not become errors that
 * trip the host's failure surface.
 */
function isModuleResolutionFailure(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  const message = err.message.toLowerCase();
  return (
    message.includes('cannot find module') ||
    message.includes('failed to resolve') ||
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('module not found')
  );
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
